import { cloneDeep, isEqual } from 'lodash';
import moment, { Moment } from 'moment';
import DossierContact from '@/contact/models/DossierContact';
import WorkStatus, { READY_WORK_STATUSES } from '@/dossier/constants/WorkStatus';
import MediaFile from '@/general/models/MediaFile';
import Severity from '@/shared/constants/Severity';
import Comment from '@/shared/models/Comment';
import Tag from '@/shared/models/Tag';
import User, { UserId } from '@/shared/models/User';
import { Base64Image, Url } from '@/shared/models/common';
import { base64ImageAsUrl } from '@/shared/utils/image';
import { isAnyOf, present } from '@/shared/utils/typescript';
import ExecutionStatus from '../constants/ExecutionStatus';
import JobCategory from '../constants/JobCategory';
import JobOrderSource from '../constants/JobOrderSource';
import JobSectionGroup from '../constants/JobSectionGroup';
import OrderStatus, { APPROVAL_ORDER_STATUSES } from '../constants/OrderStatus';
import PriceApprovalStatus, { APPROVAL_PRICE_STATUSES } from '../constants/PriceApprovalStatus';
import PriceDisplayOption from '../constants/PriceDisplayOption';
import WarrantyStatus from '../constants/WarrantyStatus';
import AbstractJob from './AbstractJob';
import JobChecklistDetails from './JobChecklistDetails';
import JobItem from './JobItem';
import Diagnostic, { ComplaintDiagnostic } from './diagnosis/Diagnostic';
import DiagnosticType from '@/jobs/constants/DiagnosticType';

export interface UpdateExecutionStatusData {
  status: ExecutionStatus,
  agree: boolean,
  duration: Moment | null,
  jobs: Array<Job>,
}

export interface CreateJobData {
  description: string,
  severity: Severity | null,
  executiveAgreement: ExecutionStatus | null,
  orderStatus: OrderStatus | null,
}

export interface JobPriceData {
  description: string;
  amountWithVat: number | null;
  amountWithoutVat: number | null;
  approvalStatus: PriceApprovalStatus;
  linkedJob: Job | null;
  warrantyStatus: WarrantyStatus;
}

export interface JobApprovalData {
  signature: Base64Image | null,
  approvalBy: DossierContact | null,
  hasPriceApprovalPermission: boolean,
}

export interface JobOrderApprovalData extends JobApprovalData {
  approvalStatus: OrderStatus,
}

export interface JobDescriptionData {
  description: string,
  severity: Severity | null,
}

export interface JobPriceApprovalData extends JobApprovalData {
  approvalStatus: PriceApprovalStatus,
}

export class JobOrder {
  status = OrderStatus.NOT_GIVEN;
  orderedOn: Moment | null = null;
  orderedBy: User | null = null;
  registeredBy: User | null = null;
  source: JobOrderSource | null = null;
  signature: Url | null = null;
  hasSignature = false;

  update(data: JobOrderApprovalData): void {
    this.status = data.approvalStatus;
    this.orderedOn = moment();
    this.orderedBy = data.approvalBy?.asUser() ?? null;
    this.signature = base64ImageAsUrl(data.signature);
    this.hasSignature = data.signature !== null;
  }
}

export class JobPrice {
  amountWithoutVat: number | null = null;
  amountWithVat: number | null = null;
  // Visible prices are prices that are visible to the customer (used during check in), these can deviate from the variables above
  visibleAmountWithoutVat: number | null = null;
  visibleAmountWithVat: number | null = null;
  dmsAmountWithoutVat: number | null = null;
  dmsAmountWithVat: number | null = null;
  vatRate: number | null = null;

  getAs(displayOption: PriceDisplayOption, ignoreDms?: boolean): number | null {
    switch (displayOption) {
      case PriceDisplayOption.WITH_VAT:
        return this.amountWithVat ?? (ignoreDms ? null : this.dmsAmountWithVat);
      case PriceDisplayOption.WITHOUT_VAT:
        return this.amountWithoutVat ?? (ignoreDms ? null : this.dmsAmountWithoutVat);
      default:
        return null;
    }
  }

  getVisibleAmountAs(displayOption: PriceDisplayOption): number | null {
    switch (displayOption) {
      case PriceDisplayOption.WITH_VAT:
        return this.visibleAmountWithVat ?? null;
      case PriceDisplayOption.WITHOUT_VAT:
        return this.visibleAmountWithoutVat ?? null;
      default:
        return null;
    }
  }

  updateWith(that: JobPrice): void {
    this.amountWithoutVat = that.amountWithVat ?? null;
    this.amountWithVat = that.amountWithVat ?? null;
    this.dmsAmountWithoutVat = that.dmsAmountWithoutVat ?? null;
    this.dmsAmountWithVat = that.dmsAmountWithVat ?? null;
  }
}

export class JobPriceApproval {
  status = PriceApprovalStatus.NOT_GIVEN;
  approvedOn: Moment | null = null;
  approvedBy: User | null = null;
  registeredBy: User | null = null;

  update(data: JobPriceApprovalData): void {
    this.status = data.approvalStatus;
    this.approvedOn = moment();
    this.approvedBy = data.approvalBy?.asUser() ?? null;
  }
}

export default class Job extends AbstractJob {
  isDeleted = false;
  order = new JobOrder();
  workStatus: WorkStatus = WorkStatus.NOT_STARTED;
  priceApproval = new JobPriceApproval();
  price = new JobPrice();
  warrantyStatus = WarrantyStatus.NO_WARRANTY;
  executionStatus = ExecutionStatus.NOT_GIVEN;
  needFollowUp = false;
  followUpResolvedOn: Moment | null = null;
  comment: Comment | null = null;
  checklistDetails: JobChecklistDetails | null = null;
  diagnostics: Array<Diagnostic> = [];
  allowedDiagnosticDuration: number | null = null;
  assignedTo: User | null = null;
  allowedMinutes: number | null = null;
  pauseReason: Tag | null = null;
  createdById: UserId | null = null;
  mediaFiles: Array<MediaFile> = [];
  createdBy: User | null = null;
  createdOn = moment();
  items: Array<JobItem> = [];
  severity: Severity | null = null;
  linkedJob: Job | null = null;
  original: Job | null = null;

  getTitle(): string {
    if (this.getCategory() === JobCategory.MECHANIC) return `${this.sequenceNumber}. ${this.description.content}`;
    return this.description.content;
  }

  isReady(): boolean {
    return this.isDeleted || READY_WORK_STATUSES.includes(this.workStatus);
  }

  hasMedia(): boolean {
    return (this.imageCount + this.videoCount + this.attachmentCount) > 0;
  }

  /**
   * Tom: we onderscheiden 4 groepen van sequencNr's momenteel:
   * < 1000 = werkzaamheden door de monteur uit te voeren
   * [1000 - 999 998] = checklijst items
   * 999 999 = DMS offerteregels
   * 1 000 000 = "no work" werkzaamheden (dat zijn regels die op het werkorder staan maar waarvoor de monteur niks moet doen, vaak algemene info zoals bv. "Klant niet bereikbaar na 14u").
   * 1 000 001 = Booking items
   */
  getCategory(): JobCategory {
    if (this.sequenceNumber === 1_000_001) return JobCategory.BOOKING_ITEM;
    if (this.sequenceNumber === 1_000_000) return JobCategory.OTHER;
    if (this.sequenceNumber === 999_999) return JobCategory.QUOTE;
    if (this.sequenceNumber >= 1000) return JobCategory.CHECKLIST;
    return JobCategory.MECHANIC;
  }

  getSectionGroup(): JobSectionGroup {
    if (this.getCategory() === JobCategory.BOOKING_ITEM) return JobSectionGroup.BOOKING;
    if (this.severity === Severity.CAUTION) return JobSectionGroup.CAUTION;
    if (this.severity === Severity.BAD) return JobSectionGroup.BAD;
    if (this.severity === Severity.GOOD) return JobSectionGroup.GOOD;
    if (this.getCategory() === JobCategory.MECHANIC && this.severity === null) return JobSectionGroup.NORMAL;
    if (this.getCategory() === JobCategory.CHECKLIST) {
      const status = this.checklistDetails?.status;
      if (status === Severity.CAUTION) return JobSectionGroup.CAUTION;
      if (status === Severity.BAD) return JobSectionGroup.BAD;
    }
    return JobSectionGroup.IGNORE;
  }

  canCopyDmsPrice(): boolean {
    return this.canChangePrice() && this.price.amountWithoutVat === null && this.price.dmsAmountWithoutVat !== null;
  }

  canChangePrice(): boolean {
    return isAnyOf(this.warrantyStatus, WarrantyStatus.NO_WARRANTY, WarrantyStatus.PARTIAL);
  }

  copyDmsPrice(): void {
    if (!this.canCopyDmsPrice()) return;
    this.price.amountWithVat = this.price.dmsAmountWithVat;
    this.price.amountWithoutVat = this.price.dmsAmountWithoutVat;
  }

  start(): void {
    this.workStatus = WorkStatus.BEING_HANDLED;
    this.pauseReason = null;
  }

  stop(reason: Tag | null = null): void {
    this.workStatus = WorkStatus.ON_HOLD;
    this.pauseReason = reason;
  }

  updateWith(that: Job): this {
    this.description = that.description;
    this.executionStatus = that.executionStatus;
    this.order = that.order;
    this.priceApproval = that.priceApproval;
    this.price.updateWith(that.price);
    this.linkedJob = that.linkedJob;
    this.warrantyStatus = that.warrantyStatus;
    this.allowedMinutes = that.allowedMinutes;
    return this;
  }

  updateWithTimeRegistration(that: Job): this {
    this.allowedMinutes = that.allowedMinutes;
    return this;
  }

  updatePrice(data: JobPriceData): this {
    this.description.content = data.description;
    this.price.amountWithoutVat = data.amountWithoutVat;
    this.price.amountWithVat = data.amountWithVat;
    this.priceApproval.status = data.approvalStatus;
    this.linkedJob = data.linkedJob;
    return this;
  }

  clone(): Job {
    const clone = cloneDeep(this);
    clone.setOriginal(this);
    return clone;
  }

  setOriginal(original: Job): void {
    this.original = original;
  }

  hasPriceChanges(that = present(this.original)): boolean {
    return !isEqual(this.price, that.price) || this.warrantyStatus !== that.warrantyStatus;
  }

  hasApprovalChanges(that = present(this.original)): boolean {
    return this.order.status !== that.order.status || this.priceApproval.status !== that.priceApproval.status;
  }

  hasChangesThatRequireAuthorisation(that = present(this.original)): boolean {
    return this.hasPriceChanges()
      || (this.order.status !== that.order.status && APPROVAL_ORDER_STATUSES.includes(this.order.status))
      || (this.priceApproval.status !== that.priceApproval.status && APPROVAL_PRICE_STATUSES.includes(this.priceApproval.status));
  }

  hasVisibleOrderStatus(): boolean {
    return this.order.status !== OrderStatus.INVISIBLE;
  }

  initPriceApprovalStatus(): void {
    if (!this.order.hasSignature && this.order.status === OrderStatus.APPROVED && this.original?.order.status === OrderStatus.NOT_GIVEN) {
      this.priceApproval.status = PriceApprovalStatus.APPROVED;
    }
  }

  initOrderStatus(): void {
    if (!this.order.hasSignature && this.order?.status === OrderStatus.NOT_GIVEN) {
      this.order.status = OrderStatus.APPROVED;
    }
  }

  getComplaintDiagnostic(): ComplaintDiagnostic | null {
    return this.diagnostics.find(d => d.type === DiagnosticType.COMPLAINT) as ComplaintDiagnostic ?? null;
  }
}